import { useState, useEffect, useRef } from "react";
import { filterCountBadgeStyle } from "@/components/CardTabs";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import { IonSkeletonText, IonButton, IonIcon } from "@ionic/react";
import { funnelOutline } from "ionicons/icons";
import TaxFormsFilterPopover, { EMPTY_TAX_FILTERS, applyTaxFormFilters, countActiveTaxFilters } from "@/components/TaxFormsFilterPopover";
import { taxYearsFor } from "@/utils/taxLayouts";
import { hasFreeDownloads, walletBalance } from "@/utils/freeDownloads";
import { useDocPrices, useLowestDocPrice } from "@/utils/docPrices";
import { generateW2Preview } from "@/utils/w2PreviewGenerator";
import { generateW9Preview } from "@/utils/w9PreviewGenerator";
import { generate1099NECPreview } from "@/utils/1099necPreviewGenerator";
import { generate1099MISCPreview } from "@/utils/1099miscPreviewGenerator";
import { generateScheduleCPreview } from "@/utils/scheduleCPreviewGenerator";
import AppTaxFormModal from "./AppTaxFormModal";
import { TAX_FORM_CONFIGS } from "./appTaxFormConfigs";
import { useDisabledGenerators, useGeneratorGate, TAX_GENERATOR_IDS } from "@/utils/generatorAvailability";

const SAMPLE_YEAR = "2024";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// Sample data used to render the card previews — the real filled IRS forms,
// same idea as the paystub template cards.
const W2_SAMPLE = {
  employeeSSN: "123-45-6789", employerEIN: "12-3456789",
  employerName: "Acme Corporation", employerAddress: "456 Business Ave",
  employerCity: "New York", employerState: "NY", employerZip: "10002",
  employeeFirstName: "John", employeeLastName: "Smith",
  employeeAddress: "123 Main Street", employeeCity: "New York", employeeState: "NY", employeeZip: "10001",
  wagesTips: 52000, federalTaxWithheld: 6240, socialSecurityWages: 52000, socialSecurityTax: 3224,
  medicareWages: 52000, medicareTax: 754, state: "NY", stateWages: 52000, stateIncomeTax: 2600,
};
const W9_SAMPLE = {
  name: "John Smith", businessName: "Smith Consulting LLC", taxClassification: "individual",
  address: "123 Main Street", city: "New York", state: "NY", zipCode: "10001",
  tinType: "ssn", ssn: "123-45-6789", signatureDate: `${SAMPLE_YEAR}-12-15`,
};
const NEC_SAMPLE = {
  payerName: "Acme Corporation", payerAddress: "456 Business Ave",
  payerCity: "New York", payerState: "NY", payerZip: "10002", payerPhone: "(555) 123-4567",
  payerTIN: "12-3456789", recipientTIN: "123-45-6789", recipientName: "John Smith",
  recipientAddress: "123 Main Street", recipientCity: "New York", recipientState: "NY", recipientZip: "10001",
  box1: 48500, box4: 0, state1: "NY", stateIncome1: 48500,
};
const MISC_SAMPLE = {
  payerName: "Acme Corporation", payerAddress: "456 Business Ave",
  payerCity: "New York", payerState: "NY", payerZip: "10002", payerPhone: "(555) 123-4567",
  payerTIN: "12-3456789", recipientTIN: "123-45-6789", recipientName: "John Smith",
  recipientAddress: "123 Main Street", recipientCity: "New York", recipientState: "NY", recipientZip: "10001",
  box1: 24000, box3: 1500,
};
const SCHEDC_SAMPLE = {
  proprietorName: "John Smith", ssn: "123-45-6789",
  principalBusiness: "Business consulting services", businessCode: "541610",
  businessName: "Smith Consulting LLC", businessAddress: "123 Main Street",
  businessCity: "New York", businessState: "NY", businessZip: "10001",
  accountingMethod: "cash", materialParticipation: "yes",
  line1: 85000, line2: 0, line3: 85000, line4: 0, line5: 85000, line7: 85000,
};

// One card per tax form: name badge + sample preview; tapping opens the
// native form modal (AppTaxFormModal).
const TAX_FORMS = [
  { key: "w2", name: "W-2", title: "W-2 Wage & Tax Statement", color: "#7c3aed",
    preview: (year) => generateW2Preview(W2_SAMPLE, year) },
  { key: "w9", name: "W-9", title: "W-9 Taxpayer Identification", color: "#0891b2",
    preview: (year) => generateW9Preview({ ...W9_SAMPLE, signatureDate: `${year}-12-15` }, year) },
  { key: "1099-nec", name: "1099-NEC", title: "1099-NEC Nonemployee Compensation", color: "#d97706",
    preview: (year) => generate1099NECPreview(NEC_SAMPLE, year) },
  { key: "1099-misc", name: "1099-MISC", title: "1099-MISC Miscellaneous Income", color: "#ea580c",
    preview: (year) => generate1099MISCPreview(MISC_SAMPLE, year) },
  { key: "schedule-c", name: "Schedule C", title: "Schedule C Profit or Loss", color: "#92400e",
    preview: (year) => generateScheduleCPreview(SCHEDC_SAMPLE, year) },
];

export default function AppTaxForms() {
  // Hero preview mode: the marketing homepage embeds this page inside a hero
  // phone — boot straight into the W-2 form modal, hide the page behind it.
  const heroPreview = new URLSearchParams(window.location.search).has("heroPreview");
  const [previews, setPreviews] = useState({});
  const [loadingPreviews, setLoadingPreviews] = useState(true);
  // ── Filter (the Purchases filter popover's twin): tax year, price, free ──
  const [filters, setFilters] = useState(EMPTY_TAX_FILTERS);
  const [filterMenu, setFilterMenu] = useState({ open: false, event: undefined });
  const activeFilterCount = countActiveTaxFilters(filters);
  // each form's years: the built-in list plus any year the admin imported a template for
  const [yearsByForm, setYearsByForm] = useState(() => Object.fromEntries(TAX_FORMS.map((f) => [f.key, TAX_FORM_CONFIGS[f.key]?.taxYears || []])));
  const [activeForm, setActiveForm] = useState(heroPreview ? "w2" : null); // key of the open form modal
  const disabledGenerators = useDisabledGenerators();
  useGeneratorGate(TAX_GENERATOR_IDS); // every tax form hidden from this visitor: back to /app
  const visibleForms = TAX_FORMS.filter(form => !disabledGenerators.has(form.key));

  useEffect(() => {
    let on = true;
    Promise.all(TAX_FORMS.map(async (f) => {
      const base = TAX_FORM_CONFIGS[f.key]?.taxYears || [];
      try { return [f.key, await taxYearsFor(f.key, base)]; } catch { return [f.key, base]; }
    })).then((pairs) => { if (on) setYearsByForm(Object.fromEntries(pairs)); });
    return () => { on = false; };
  }, []);
  const allYears = [...new Set(Object.values(yearsByForm).flat())].sort((a, b) => Number(b) - Number(a));
  // the signed-in person, for "free for you": a plan with downloads left, or a balance that covers the price
  const [user, setUser] = useState(() => { try { return JSON.parse(localStorage.getItem("userInfo") || "null"); } catch { return null; } });
  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (!token) return undefined;
    let on = true;
    fetch(`${BACKEND_URL}/api/user/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (on && d && d.success && d.user) setUser(d.user); })
      .catch(() => {});
    return () => { on = false; };
  }, []);
  const priceOverrides = useDocPrices(); // the admin's prices (Preferences > Document prices)
  const seoPrice = useLowestDocPrice(["w2", "w9", "1099-nec", "1099-misc", "schedule-c"]);
  const priceOf = (form) => { const o = priceOverrides[form.key]; if (typeof o === "number") return o; const p = TAX_FORM_CONFIGS[form.key]?.price; return typeof p === "function" ? p({}) : (p || 0); };
  // free for anyone when the form costs nothing; free for the signed-in person when a plan or balance covers it
  const isFree = (form) => priceOf(form) === 0 || (!!user && (hasFreeDownloads(user) || walletBalance(user) >= priceOf(form)));
  const filteredForms = applyTaxFormFilters(visibleForms, filters, { yearsByForm, priceOf, isFree });

  // The cards show the filter's year (the sample year without one); each
  // form's image is rendered once per year and kept.
  const previewYear = filters.taxYear || SAMPLE_YEAR;
  const previewCache = useRef({});
  useEffect(() => {
    if (heroPreview) return undefined; // hero phone never shows the cards
    let cancelled = false;
    const forms = TAX_FORMS.filter((f) => !filters.taxYear || (yearsByForm[f.key] || []).includes(filters.taxYear));
    const cached = Object.fromEntries(forms.filter((f) => previewCache.current[`${f.key}:${previewYear}`]).map((f) => [f.key, previewCache.current[`${f.key}:${previewYear}`]]));
    setPreviews(cached);
    const todo = forms.filter((f) => !cached[f.key]);
    if (todo.length === 0) { setLoadingPreviews(false); return undefined; }
    setLoadingPreviews(true);
    (async () => {
      for (const form of todo) {
        try {
          const img = await form.preview(previewYear);
          if (cancelled) return;
          if (img) {
            previewCache.current[`${form.key}:${previewYear}`] = img;
            setPreviews(prev => ({ ...prev, [form.key]: img }));
          }
        } catch (err) {
          console.error(`Tax form preview failed for ${form.key}:`, err);
        }
      }
      if (!cancelled) setLoadingPreviews(false);
    })();
    return () => { cancelled = true; };
  }, [previewYear, yearsByForm]); // eslint-disable-line

  return (
    <AppLayout fillHeight>
      <AppSeo title="Tax Form Generator: W-2, W-9 & 1099" description="Create accurate W-2, W-9, 1099-NEC, 1099-MISC, and Schedule C forms in minutes with instant PDF download." path="/app/tax-forms" price={seoPrice} appName="MintSlip Tax Form Generator" />
      <div style={{ padding: 10, height: "100%", boxSizing: "border-box", ...(heroPreview ? { display: "none" } : null) }}>
        <div style={{ background: "var(--ion-card-background)", borderRadius: 6, padding: "20px 20px 24px", height: "100%", overflowY: "auto", boxShadow: "0 2px 12px rgba(0,0,0,0.10)", boxSizing: "border-box" }}>
          {/* Filter, as the Purchases page wears it */}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 14 }}>
            <IonButton fill="solid" size="small" onClick={(e) => setFilterMenu({ open: true, event: e.nativeEvent })} style={{ "--background": "var(--ion-background-color)", "--color": "var(--ion-text-color)" }}>
              <span slot="start" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1rem", marginInlineEnd: 6 }}>
                <IonIcon icon={funnelOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
              </span>
              Filter
              {activeFilterCount > 0 && (
                <span className="filter-count" style={filterCountBadgeStyle}>{activeFilterCount}</span>
              )}
            </IonButton>
          </div>
          {visibleForms.length > 0 && filteredForms.length === 0 && (
            <p style={{ color: "var(--ion-color-medium)", fontSize: "0.9rem", textAlign: "center", padding: "32px 0" }}>
              No forms match these filters.{filters.free === "free" && !user ? " No tax form is free at the moment. Sign in to see what a plan or balance covers." : ""}{" "}
              <button type="button" onClick={() => setFilters(EMPTY_TAX_FILTERS)} style={{ background: "none", border: "none", color: "var(--ion-color-primary)", fontWeight: 600, cursor: "pointer", padding: 0, font: "inherit" }}>Clear filters</button>
            </p>
          )}
          {visibleForms.length === 0 && !loadingPreviews && (
            <p style={{ color: "var(--ion-color-medium)", fontSize: "0.9rem", textAlign: "center", padding: "32px 0" }}>
              No forms are currently available.
            </p>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
            {filteredForms.map((form, cardIdx) => (
              <div key={form.key} className="app-sample-card ion-activatable"
                onClick={() => setActiveForm(form.key)}
                style={{ animationDelay: `${cardIdx * 60}ms`, cursor: "pointer", borderRadius: 10, border: "1.5px solid var(--app-divider, rgba(0,0,0,0.12))", background: "var(--ion-card-background)", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", transition: "box-shadow 0.2s, transform 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.18)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"; e.currentTarget.style.transform = "none"; }}
              >
                <ion-ripple-effect />
                <div style={{ position: "relative", background: "#fff", overflow: "hidden", minHeight: 160 }}>
                  {/* Form name badge over the preview (paystub-card style) */}
                  <div style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: form.color, color: "#fff", padding: "4px 12px", borderRadius: 999, fontSize: "0.72rem", fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,0.28)" }}>
                    {form.name}
                  </div>
                  {previews[form.key] ? (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden", pointerEvents: "none" }}>
                      <img src={previews[form.key]} alt={`${form.name} sample`} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
                      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 56, background: "linear-gradient(to top, rgba(0,0,0,0.28), transparent)" }} />
                    </div>
                  ) : loadingPreviews ? (
                    <div style={{ position: "relative", paddingTop: "141.4%", overflow: "hidden" }}>
                      <IonSkeletonText animated={true} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", margin: 0, borderRadius: 0, "--background": "rgba(0, 0, 0, 0.085)", "--background-rgb": "18, 18, 18" }} />
                    </div>
                  ) : (
                    <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", background: "#f9fafb" }}>
                      <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>Preview unavailable</span>
                    </div>
                  )}
                </div>
                <div style={{ padding: "8px 16px 12px", textAlign: "center", borderTop: "1px solid var(--app-divider, rgba(0,0,0,0.06))" }}>
                  <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--ion-color-primary)" }}>Create {filters.taxYear ? `${filters.taxYear} ` : ""}{form.name} →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <TaxFormsFilterPopover open={filterMenu.open} event={filterMenu.event} onClose={() => setFilterMenu({ open: false, event: undefined })}
        filters={filters} onApply={setFilters} years={allYears} />

      {/* ── Native form modal (same flow as the paystub modal) ── */}
      {activeForm && <AppTaxFormModal config={TAX_FORM_CONFIGS[activeForm]} initialYear={filters.taxYear || null} onClose={() => setActiveForm(null)} />}
    </AppLayout>
  );
}
